import { memo, useLayoutEffect, useRef } from 'react';

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { LANDING_SCROLL_ROOT_ID } from '@pages/LandingPage/model/scroll';

import { LandingSphereDiv } from './LandingSphereDiv';

interface LandingSpheresAnimationProps {
  sceneIndex: number;
}

interface SphereSceneParameters {
  scale: number;
  transformOrigin: string;
  xPercent: number;
  yPercent: number;
}

interface SpheresSceneConfig {
  sphereA: SphereSceneParameters;
  sphereB: SphereSceneParameters;
}

const sphereCompositionParallaxQuery = '(min-width: 64rem)';
const sphereCompositionStaticQuery = '(max-width: 63.999rem)';
const sphereCompositionStartYPercent = 40;
const sphereCompositionEndYPercent = 0;

const unchangedSphereParameters = {
  scale: 1,
  transformOrigin: '50% 50%',
  xPercent: 0,
  yPercent: 0,
} satisfies SphereSceneParameters;

const unchangedSphereSceneConfig = {
  sphereA: unchangedSphereParameters,
  sphereB: unchangedSphereParameters,
} satisfies SpheresSceneConfig;

const sphereSceneConfigs = [
  unchangedSphereSceneConfig,
  {
    sphereA: {
      scale: 2 / 3,
      transformOrigin: '50% 100%',
      xPercent: 0,
      yPercent: 0,
    },
    sphereB: {
      scale: 1 / 3,
      transformOrigin: '50% 100%',
      xPercent: 0,
      yPercent: 0,
    },
  },
  {
    sphereA: {
      scale: 2 / 3,
      transformOrigin: '50% 50%',
      xPercent: 0,
      yPercent: 0,
    },
    sphereB: {
      scale: 1 / 3,
      transformOrigin: '50% 50%',
      xPercent: 0,
      yPercent: 0,
    },
  },
  {
    sphereA: {
      scale: 1 / 2,
      transformOrigin: '50% 50%',
      xPercent: -25,
      yPercent: 0,
    },
    sphereB: {
      scale: 1 / 2,
      transformOrigin: '50% 50%',
      xPercent: 25,
      yPercent: 0,
    },
  },
] as const satisfies readonly SpheresSceneConfig[];

const orbitingSphereSceneIndex = sphereSceneConfigs.length - 1;
const sphereOrbitDuration = 14;

gsap.registerPlugin(ScrollTrigger);

const getSphereSceneConfig = (sceneIndex: number) => {
  const normalizedSceneIndex = Math.min(
    sphereSceneConfigs.length - 1,
    Math.max(0, Math.trunc(sceneIndex))
  );

  return sphereSceneConfigs[normalizedSceneIndex] ?? unchangedSphereSceneConfig;
};

const parseTransformOriginPart = (value: string) => {
  const normalizedValue = value.trim().toLowerCase();

  if (normalizedValue === 'left' || normalizedValue === 'top') {
    return 0;
  }

  if (normalizedValue === 'center') {
    return 0.5;
  }

  if (normalizedValue === 'right' || normalizedValue === 'bottom') {
    return 1;
  }

  if (normalizedValue.endsWith('%')) {
    const percentValue = Number.parseFloat(normalizedValue);

    return Number.isFinite(percentValue) ? percentValue / 100 : 0.5;
  }

  return 0.5;
};

const parseTransformOrigin = (transformOrigin: string) => {
  const [xOrigin = '50%', yOrigin = '50%'] = transformOrigin.split(/\s+/);

  return {
    x: parseTransformOriginPart(xOrigin),
    y: parseTransformOriginPart(yOrigin),
  };
};

const getGsapNumberProperty = (
  target: HTMLElement,
  property: 'scale' | 'xPercent' | 'yPercent',
  fallbackValue: number
) => {
  const value = gsap.getProperty(target, property);
  const numericValue = typeof value === 'number' ? value : Number.parseFloat(value);

  return Number.isFinite(numericValue) ? numericValue : fallbackValue;
};

const getOriginCompensatedPercent = (
  currentPercent: number,
  currentScale: number,
  currentOrigin: number,
  nextOrigin: number
) => currentPercent + (1 - currentScale) * (currentOrigin - nextOrigin) * 100;

const addSphereSceneTween = (
  timeline: gsap.core.Timeline,
  target: HTMLElement,
  currentTransformOrigin: string,
  nextParameters: SphereSceneParameters
) => {
  const currentScale = getGsapNumberProperty(target, 'scale', unchangedSphereParameters.scale);
  const currentXPercent = getGsapNumberProperty(
    target,
    'xPercent',
    unchangedSphereParameters.xPercent
  );
  const currentYPercent = getGsapNumberProperty(
    target,
    'yPercent',
    unchangedSphereParameters.yPercent
  );
  const currentOrigin = parseTransformOrigin(currentTransformOrigin);
  const nextOrigin = parseTransformOrigin(nextParameters.transformOrigin);

  gsap.set(target, {
    transformOrigin: nextParameters.transformOrigin,
    xPercent: getOriginCompensatedPercent(
      currentXPercent,
      currentScale,
      currentOrigin.x,
      nextOrigin.x
    ),
    yPercent: getOriginCompensatedPercent(
      currentYPercent,
      currentScale,
      currentOrigin.y,
      nextOrigin.y
    ),
  });

  timeline.to(target, nextParameters, 0);
};

const LandingSpheresAnimationComponent = ({ sceneIndex }: LandingSpheresAnimationProps) => {
  const compositionRef = useRef<HTMLElement | null>(null);
  const sphereAOrbitRef = useRef<HTMLDivElement | null>(null);
  const sphereARef = useRef<HTMLDivElement | null>(null);
  const sphereACounterRotationRef = useRef<HTMLDivElement | null>(null);
  const sphereBOrbitRef = useRef<HTMLDivElement | null>(null);
  const sphereBRef = useRef<HTMLDivElement | null>(null);
  const sphereBCounterRotationRef = useRef<HTMLDivElement | null>(null);
  const sphereTransformOriginsRef = useRef({
    sphereA: unchangedSphereParameters.transformOrigin,
    sphereB: unchangedSphereParameters.transformOrigin,
  });

  useLayoutEffect(() => {
    const composition = compositionRef.current;
    const scrollRoot = document.getElementById(LANDING_SCROLL_ROOT_ID);
    const heroSection = document.getElementById('hero');
    const visionSection = document.getElementById('vision');

    if (!composition || !scrollRoot || !heroSection || !visionSection) {
      return;
    }

    const mediaContext = gsap.matchMedia(composition);

    mediaContext.add(sphereCompositionParallaxQuery, () => {
      gsap.fromTo(
        composition,
        {
          yPercent: sphereCompositionStartYPercent,
        },
        {
          yPercent: sphereCompositionEndYPercent,
          ease: 'none',
          scrollTrigger: {
            scroller: scrollRoot,
            trigger: heroSection,
            start: 'top top',
            endTrigger: visionSection,
            end: 'top top',
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );
    });

    mediaContext.add(sphereCompositionStaticQuery, () => {
      gsap.set(composition, {
        yPercent: sphereCompositionEndYPercent,
      });
    });

    return () => {
      mediaContext.revert();
    };
  }, []);

  useLayoutEffect(() => {
    const sphereAOrbit = sphereAOrbitRef.current;
    const sphereACounterRotation = sphereACounterRotationRef.current;
    const sphereBOrbit = sphereBOrbitRef.current;
    const sphereBCounterRotation = sphereBCounterRotationRef.current;

    if (!sphereAOrbit || !sphereACounterRotation || !sphereBOrbit || !sphereBCounterRotation) {
      return;
    }

    const orbitTargets = [sphereAOrbit, sphereBOrbit];
    const counterRotationTargets = [sphereACounterRotation, sphereBCounterRotation];

    gsap.killTweensOf([...orbitTargets, ...counterRotationTargets]);
    gsap.set(orbitTargets, {
      transformOrigin: '50% 50%',
    });
    gsap.set(counterRotationTargets, {
      transformOrigin: '50% 50%',
    });

    if (sceneIndex !== orbitingSphereSceneIndex) {
      const resetTween = gsap.to([...orbitTargets, ...counterRotationTargets], {
        rotation: 0,
        duration: 0.8,
        ease: 'power3.out',
      });

      return () => {
        resetTween.kill();
      };
    }

    const orbitTimeline = gsap.timeline({
      defaults: {
        ease: 'none',
        repeat: -1,
      },
    });

    orbitTimeline
      .to(
        sphereAOrbit,
        {
          rotation: '+=360',
          duration: sphereOrbitDuration,
        },
        0
      )
      .to(
        sphereBOrbit,
        {
          rotation: '+=360',
          duration: sphereOrbitDuration,
        },
        0
      )
      .to(
        counterRotationTargets,
        {
          rotation: '-=360',
          duration: sphereOrbitDuration,
        },
        0
      );

    return () => {
      orbitTimeline.kill();
    };
  }, [sceneIndex]);

  useLayoutEffect(() => {
    const sphereA = sphereARef.current;
    const sphereB = sphereBRef.current;

    if (!sphereA || !sphereB) {
      return;
    }

    const sceneConfig = getSphereSceneConfig(sceneIndex);
    const timeline = gsap.timeline({
      defaults: {
        duration: 0.8,
        ease: 'power3.out',
      },
    });

    addSphereSceneTween(
      timeline,
      sphereA,
      sphereTransformOriginsRef.current.sphereA,
      sceneConfig.sphereA
    );
    addSphereSceneTween(
      timeline,
      sphereB,
      sphereTransformOriginsRef.current.sphereB,
      sceneConfig.sphereB
    );

    sphereTransformOriginsRef.current = {
      sphereA: sceneConfig.sphereA.transformOrigin,
      sphereB: sceneConfig.sphereB.transformOrigin,
    };

    return () => {
      timeline.kill();
    };
  }, [sceneIndex]);

  return (
    <div
      className="
        pointer-events-none sticky top-(--landing-sphere-top-offset) mx-auto flex w-full
        max-w-screen-2xl justify-center px-4
        lg:justify-end
      "
    >
      <figure
        ref={compositionRef}
        className="pointer-events-none relative size-(--landing-sphere-size) shrink-0"
      >
        <LandingSphereDiv />
        <div ref={sphereAOrbitRef} className="absolute inset-0">
          <div ref={sphereARef} className="absolute inset-0">
            <div ref={sphereACounterRotationRef} className="absolute inset-0">
              <LandingSphereDiv />
            </div>
          </div>
        </div>
        <div ref={sphereBOrbitRef} className="absolute inset-0">
          <div ref={sphereBRef} className="absolute inset-0">
            <div ref={sphereBCounterRotationRef} className="absolute inset-0">
              <LandingSphereDiv />
            </div>
          </div>
        </div>
      </figure>
    </div>
  );
};

export const LandingSpheresAnimation = memo(
  LandingSpheresAnimationComponent,
  (previousProps, nextProps) => previousProps.sceneIndex === nextProps.sceneIndex
);
